import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, CalendarClock, Wrench, Info } from "lucide-react";

const FACILITIES = [
  "badminton", "indoor-box", "indoor-net", "bowling-machine",
  "gym", "swimming", "cricket-ground", "open-net",
];

const STATE_META = {
  active:   { color: "border-red-500/60 bg-red-950/30 text-red-300",         label: "ACTIVE NOW" },
  upcoming: { color: "border-amber-500/60 bg-amber-950/30 text-amber-300",   label: "UPCOMING" },
  past:     { color: "border-white/15 bg-black/20 text-white/40",            label: "PAST" },
};

const localDatetime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function AdminDowntimes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({
    facility_slug: "badminton", start_at: localDatetime(),
    end_at: localDatetime(), reason: "",
  });

  const reload = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/downtimes");
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/downtimes", {
        facility_slug: form.facility_slug,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        reason: form.reason,
      });
      toast.success("Downtime scheduled");
      setModal(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Remove downtime for ${d.facility_slug}?`)) return;
    try {
      await api.delete(`/admin/downtimes/${d.id}`);
      toast.success("Downtime removed");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-5 md:p-8" data-testid="admin-downtimes">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Scheduled Downtime</div>
          <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Facility Maintenance</h1>
          <p className="text-sm text-white/60 mt-1">Hide a facility during a specific date/time window. It reopens automatically.</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="new-downtime-btn"
          className="bg-brand-orange text-black px-4 py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center gap-2">
          <Plus size={14} /> Schedule Downtime
        </button>
      </div>

      <div className="border border-white/10 bg-brand-blue/8 p-4 mb-6 flex gap-3 text-sm">
        <Info size={16} className="brand-blue shrink-0 mt-0.5" />
        <div className="text-white/70">
          During an active downtime the facility is <strong>removed from the public site</strong>, the booking page grid shows &quot;Under Maintenance&quot;, and direct URL access returns 404. Once the end time passes, everything reopens automatically.
        </div>
      </div>

      {loading ? <div className="text-white/40 py-16 text-center font-mono text-xs uppercase tracking-widest">Loading…</div> :
      items.length === 0 ? (
        <div className="border border-dashed border-white/15 p-10 text-center text-white/50 text-sm">
          No scheduled downtimes.
        </div>
      ) : (
        <div className="space-y-3" data-testid="downtimes-list">
          {items.map((d) => {
            const meta = STATE_META[d.state] || STATE_META.past;
            const s = new Date(d.start_at); const e = new Date(d.end_at);
            return (
              <div key={d.id} className={`border ${meta.color} p-4 flex items-start justify-between gap-3`} data-testid={`downtime-${d.id}`}>
                <div className="flex gap-3">
                  <div className={`w-10 h-10 shrink-0 grid place-items-center border ${meta.color}`}>
                    {d.state === "active" ? <Wrench size={16} /> : <CalendarClock size={16} />}
                  </div>
                  <div>
                    <div className="font-display text-base font-black uppercase">{d.facility_name || d.facility_slug}</div>
                    <div className="text-[11px] font-mono uppercase tracking-widest mt-0.5">
                      <span className={`px-2 py-0.5 border ${meta.color}`}>{meta.label}</span>
                    </div>
                    <div className="text-sm text-white/70 mt-1.5">
                      <span className="font-mono">{s.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="text-white/40 mx-2">→</span>
                      <span className="font-mono">{e.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {d.reason && <div className="text-[12px] text-white/50 mt-1">{d.reason}</div>}
                  </div>
                </div>
                <button onClick={() => del(d)} className="border border-red-500/40 text-red-400 px-3 py-2 hover:bg-red-500 hover:text-white"><Trash2 size={12} /></button>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setModal(false)}>
          <form onSubmit={submit} className="w-full max-w-md bg-[#131A2B] border border-white/12 my-8" onClick={(e) => e.stopPropagation()} data-testid="downtime-modal">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Schedule Downtime</div>
                <div className="font-display text-lg font-black uppercase mt-0.5">Close a facility temporarily</div>
              </div>
              <button type="button" onClick={() => setModal(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Facility</label>
                <select value={form.facility_slug} onChange={(e) => setForm({ ...form, facility_slug: e.target.value })}
                  data-testid="dt-facility"
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                  {FACILITIES.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Start</label>
                  <input required type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                    data-testid="dt-start"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">End</label>
                  <input required type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })}
                    data-testid="dt-end"
                    className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Reason (shown internally)</label>
                <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="e.g. Court flooring repair"
                  data-testid="dt-reason"
                  className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-white/10 flex gap-2">
              <button type="button" onClick={() => setModal(false)} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
              <button type="submit" data-testid="dt-save-btn" className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center justify-center gap-2">
                <Save size={12} /> Schedule
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
